// Derwent Hub Admin — broker/admin management for My Derwent Hub client portal.
// Auth-protected route at /derwent-hub.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import StartOnboardingDialog from '../components/onboarding/StartOnboardingDialog';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ChevronLeft, Plus, Search, Loader2, Copy, RefreshCw, User, Send,
  Building2, ArrowRight, Trash2, X, ChevronRight, FileText, Sparkles,
  CheckCircle2, Clock, TrendingUp, Home as HomeIcon, MessageSquare, Target,
  ShieldCheck, StickyNote, History, AlertTriangle, Flag, LayoutDashboard,
  Filter, Zap, Gem, Percent, CalendarClock,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import Header from '../components/Header';
import BetaBadge from '../components/BetaBadge';

const API = process.env.REACT_APP_BACKEND_URL;

const STAGE_BADGE = {
  enquiry_received:        'bg-slate-100 text-slate-700',
  strategy_session_booked: 'bg-sky-100 text-sky-800',
  documents_requested:     'bg-amber-100 text-amber-800',
  documents_received:      'bg-amber-100 text-amber-800',
  pre_assessment:          'bg-purple-100 text-purple-800',
  options_being_prepared:  'bg-purple-100 text-purple-800',
  application_ready:       'bg-indigo-100 text-indigo-800',
  submitted_to_lender:     'bg-indigo-100 text-indigo-800',
  credit_assessment:       'bg-blue-100 text-blue-800',
  conditional_approval:    'bg-emerald-100 text-emerald-800',
  valuation_ordered:       'bg-blue-100 text-blue-800',
  formal_approval:         'bg-emerald-100 text-emerald-800',
  loan_documents_issued:   'bg-teal-100 text-teal-800',
  loan_documents_signed:   'bg-teal-100 text-teal-800',
  settlement_booked:       'bg-emerald-100 text-emerald-800',
  settled:                 'bg-emerald-500 text-white',
  post_settlement_review:  'bg-slate-200 text-slate-800',
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return iso; }
};
const fmtDateTime = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-AU', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
};

export default function DerwentHubAdmin() {
  const { token: authToken } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${authToken}` }), [authToken]);
  const [items, setItems] = useState([]);
  const [stages, setStages] = useState([]);
  const [stageLabels, setStageLabels] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStage, setFilterStage] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [onboardOpen, setOnboardOpen] = useState(false);
  const navigate = useNavigate();
  const [selectedId, setSelectedId] = useState(null);
  const [view, setView] = useState('overview'); // overview | clients | content
  const [smartFilter, setSmartFilter] = useState(''); // '' | docs_review | urgent | stale | bank_eta_today | idv_incomplete | settlements_upcoming | post_settle | new_requests

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/hub/clients`, { headers });
      setItems(data.items || []);
      setStages(data.stages || []);
      setStageLabels(data.stage_labels || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load');
    } finally { setLoading(false); }
  }, [headers]);

  useEffect(() => { load(); }, [load]);

  const now = new Date();
  const twoDaysAgoIso = new Date(now.getTime() - 48 * 3600 * 1000).toISOString();
  const twoWeeksIso = new Date(now.getTime() + 14 * 86400 * 1000).toISOString();
  const startTodayIso = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString();
  const endTodayIso = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59).toISOString();

  const filtered = items.filter((c) => {
    if (filterStage && c.stage !== filterStage) return false;
    // Smart filters
    if (smartFilter === 'docs_review' && (c.docs_awaiting_review || 0) === 0) return false;
    if (smartFilter === 'docs_outstanding' && (c.docs_outstanding || 0) === 0) return false;
    if (smartFilter === 'urgent' && !c.is_urgent) return false;
    if (smartFilter === 'stale') {
      const stale = c.updated_at && c.updated_at < twoDaysAgoIso;
      if (!stale || c.stage === 'settled' || c.stage === 'post_settlement_review') return false;
    }
    if (smartFilter === 'bank_eta_today') {
      const eta = c.expected_bank_response;
      if (!eta || eta < startTodayIso || eta > endTodayIso) return false;
    }
    if (smartFilter === 'idv_incomplete') {
      const idv = c.id_verification;
      if (idv && (idv.status_key === 'completed' || idv.status_key === 'report_available')) return false;
    }
    if (smartFilter === 'settlements_upcoming') {
      const sd = c.loan?.settlement_date;
      if (!sd || sd < startTodayIso.slice(0, 10) || sd > twoWeeksIso.slice(0, 10)) return false;
    }
    if (smartFilter === 'post_settle' && c.stage !== 'post_settlement_review') return false;
    if (smartFilter === 'new_requests' && (c.new_requests || 0) === 0) return false;
    if (smartFilter === 'high_priority' && c.priority !== 'high') return false;

    if (search) {
      const q = search.toLowerCase();
      const nm = `${c.first_name || ''} ${c.last_name || ''} ${c.email || ''} ${c.mobile || ''} ${c.reference_number || ''} ${c.loan?.property_address || ''} ${c.loan?.lender || ''}`.toLowerCase();
      if (!nm.includes(q)) return false;
    }
    return true;
  });

  const counts = useMemo(() => {
    const counts = {};
    for (const s of stages) counts[s] = 0;
    for (const c of items) counts[c.stage] = (counts[c.stage] || 0) + 1;
    return counts;
  }, [items, stages]);

  return (
    <div className="min-h-screen bg-[var(--page-bg)]">
      <Header />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <Link to="/" className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">
              <ChevronLeft className="w-3.5 h-3.5" /> Home
            </Link>
            <h1 className="text-2xl sm:text-3xl font-black text-slate-900 mt-1 inline-flex items-center gap-2.5 flex-wrap">
              My Derwent Hub — Admin
              <BetaBadge size="sm" />
            </h1>
            <p className="text-sm text-slate-600 mt-1">Every client&apos;s home-loan journey, live.</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setOnboardOpen(true)}
              data-testid="dhub-start-onboarding"
              className="inline-flex items-center gap-2 rounded-full bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold px-4 py-2.5 shadow-sm"
            >
              <ShieldCheck className="w-4 h-4" /> Start Client Portal
            </button>
            <button
              type="button"
              onClick={() => navigate('/esign?new=1')}
              data-testid="dhub-send-esign"
              className="inline-flex items-center gap-2 rounded-full bg-violet-600 hover:bg-violet-700 text-white text-sm font-semibold px-4 py-2.5 shadow-sm"
            >
              <FileText className="w-4 h-4" /> Send E-Sign Request
            </button>
          {view === 'clients' && (
            <button
              type="button"
              onClick={() => setCreateOpen(true)}
              data-testid="dhub-create-client"
              className="inline-flex items-center gap-2 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold px-4 py-2.5 shadow-sm"
            >
              <Plus className="w-4 h-4" /> Add client
            </button>
          )}
          </div>
        </div>
        <StartOnboardingDialog open={onboardOpen} onOpenChange={setOnboardOpen} onCreated={(id) => navigate(`/onboarding/${id}`)} />

        {/* View toggle */}
        <div className="mb-4 inline-flex items-center gap-1 rounded-full bg-slate-100 p-1">
          {[
            { key: 'overview', label: 'Command Centre', testId: 'dhub-view-overview' },
            { key: 'clients', label: 'Clients', testId: 'dhub-view-clients' },
            { key: 'content', label: 'Content (FAQs · How-Tos)', testId: 'dhub-view-content' },
          ].map((v) => (
            <button
              key={v.key}
              type="button"
              onClick={() => { setView(v.key); setSmartFilter(''); }}
              data-testid={v.testId}
              className={`rounded-full text-xs font-semibold px-3.5 py-1.5 transition-colors ${
                view === v.key ? 'bg-slate-900 text-white' : 'text-slate-700 hover:text-slate-900'
              }`}
            >
              {v.label}
            </button>
          ))}
        </div>

        {view === 'overview' ? (
          <CommandCentre
            headers={headers}
            onDrillDown={(filter) => { setView('clients'); setSmartFilter(filter); }}
            onOpenClient={(id) => setSelectedId(id)}
            onNewClient={() => setCreateOpen(true)}
          />
        ) : view === 'content' ? (
          <ContentManager headers={headers} />
        ) : (
        <>
        {/* Stage funnel counts (mini strip) */}
        <div className="mb-4 flex items-center gap-1.5 overflow-x-auto pb-1">
          {stages.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setFilterStage(filterStage === s ? '' : s)}
              className={`whitespace-nowrap rounded-full text-[11px] font-semibold px-3 py-1.5 transition-colors ${
                filterStage === s ? 'bg-slate-900 text-white' : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50'
              }`}
            >
              {stageLabels[s] || s} <span className="text-slate-400">({counts[s] || 0})</span>
            </button>
          ))}
          {filterStage && (
            <button type="button" onClick={() => setFilterStage('')} className="text-[11px] text-slate-500 hover:text-slate-900">Clear</button>
          )}
        </div>

        {/* Smart filter chips */}
        {smartFilter && (
          <div className="mb-3 flex items-center gap-2 rounded-xl bg-slate-900 text-white px-3 py-2">
            <Filter className="w-3.5 h-3.5" />
            <p className="text-xs font-semibold flex-1">
              Filtered by: <span className="font-black">{SMART_FILTER_LABELS[smartFilter] || smartFilter}</span> · {filtered.length} client{filtered.length === 1 ? '' : 's'}
            </p>
            <button type="button" onClick={() => setSmartFilter('')} className="rounded-full bg-white/10 hover:bg-white/20 text-[11px] font-semibold px-2 py-1">Clear</button>
          </div>
        )}

        {/* Search bar */}
        <div className="mb-4 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, email, phone, ref, address or lender…"
            data-testid="dhub-search"
            className="w-full pl-10 pr-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10"
          />
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16 text-slate-500">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16">
            <User className="w-8 h-8 mx-auto text-slate-400 mb-2" />
            <p className="text-sm text-slate-600">No clients match — try adjusting the filters.</p>
            <button
              type="button"
              onClick={() => setCreateOpen(true)}
              className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-slate-900 text-white text-xs font-semibold px-3 py-2"
            >
              <Plus className="w-3.5 h-3.5" /> Add your first client
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {filtered.map((c) => (
              <ClientCard key={c.id} client={c} stageLabels={stageLabels} onOpen={() => setSelectedId(c.id)} />
            ))}
          </div>
        )}
        </>
        )}
      </div>

      {createOpen && (
        <CreateClientDialog
          headers={headers}
          stages={stages}
          stageLabels={stageLabels}
          onClose={() => setCreateOpen(false)}
          onCreated={(newClient) => { setCreateOpen(false); load(); setSelectedId(newClient.id); }}
        />
      )}
      {selectedId && (
        <ClientDetailDrawer
          clientId={selectedId}
          headers={headers}
          stages={stages}
          stageLabels={stageLabels}
          onClose={() => setSelectedId(null)}
          onChanged={load}
        />
      )}
    </div>
  );
}

// ─── Client card in the grid ────────────────────────────────────────
const SMART_FILTER_LABELS = {
  docs_review: 'Documents waiting for review',
  docs_outstanding: 'Docs outstanding from client',
  urgent: 'Flagged as urgent',
  stale: 'No update > 48h',
  bank_eta_today: 'Bank ETA due today',
  idv_incomplete: 'ID verification incomplete',
  settlements_upcoming: 'Settlements in next 14 days',
  post_settle: 'Post-settlement reviews',
  new_requests: 'New client requests',
  high_priority: 'High priority',
};

const ClientCard = ({ client, stageLabels, onOpen }) => (
  <button
    type="button"
    onClick={onOpen}
    data-testid={`dhub-client-${client.id}`}
    className={`text-left rounded-2xl border bg-white hover:shadow-md p-4 transition-all relative ${
      client.is_urgent ? 'border-red-300 ring-1 ring-red-200' : 'border-slate-200 hover:border-slate-400'
    }`}
  >
    {client.is_urgent && (
      <span className="absolute -top-2 -right-2 rounded-full bg-red-500 text-white text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 shadow">
        Urgent
      </span>
    )}
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0 flex-1">
        <p className="text-sm font-black text-slate-900 truncate">{client.first_name} {client.last_name}</p>
        <p className="text-[11px] text-slate-500 truncate">{client.email}</p>
        {client.source === 'strategy_session' && (
          <span className="mt-1 inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-800 rounded-full px-1.5 py-0.5" data-testid={`dhub-source-${client.id}`}>
            <Sparkles className="w-2.5 h-2.5" /> Strategy Session
          </span>
        )}
      </div>
      <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full whitespace-nowrap ${STAGE_BADGE[client.stage] || 'bg-slate-100 text-slate-700'}`}>
        {stageLabels[client.stage] || client.stage}
      </span>
    </div>
    <div className="mt-2 grid grid-cols-3 gap-1.5 text-[10px]">
      {(client.docs_outstanding || 0) > 0 && (
        <span className="rounded bg-amber-50 border border-amber-200 text-amber-800 font-semibold px-1.5 py-0.5 text-center">
          {client.docs_outstanding} doc{client.docs_outstanding === 1 ? '' : 's'} due
        </span>
      )}
      {(client.docs_awaiting_review || 0) > 0 && (
        <span className="rounded bg-sky-50 border border-sky-200 text-sky-800 font-semibold px-1.5 py-0.5 text-center">
          {client.docs_awaiting_review} to review
        </span>
      )}
      {(client.new_requests || 0) > 0 && (
        <span className="rounded bg-purple-50 border border-purple-200 text-purple-800 font-semibold px-1.5 py-0.5 text-center">
          {client.new_requests} new req
        </span>
      )}
    </div>
    <div className="mt-3 grid grid-cols-2 gap-2 text-[11px] text-slate-600">
      <div>
        <p className="uppercase tracking-wider text-[9px] text-slate-400">Bank ETA</p>
        <p className="font-semibold text-slate-900">{fmtDate(client.expected_bank_response)}</p>
      </div>
      <div>
        <p className="uppercase tracking-wider text-[9px] text-slate-400">Ref</p>
        <p className="font-mono text-slate-900 text-[10px]">{client.reference_number}</p>
      </div>
    </div>
  </button>
);

// ─── Create client dialog ───────────────────────────────────────────
const CreateClientDialog = ({ headers, stages, stageLabels, onClose, onCreated }) => {
  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', mobile: '',
    initial_stage: 'enquiry_received', initial_notes: '',
  });
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);

  const submit = async () => {
    if (!form.first_name.trim() || !form.last_name.trim() || !form.email.trim()) {
      toast.error('Name and email are required'); return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/hub/clients`, form, { headers });
      setCreated(data);
      toast.success('Client hub created');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create');
    } finally { setBusy(false); }
  };

  const copyLink = async () => {
    const url = `${window.location.origin}/hub/${created.magic_token}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied — paste in email or SMS');
    } catch {
      toast.info('Copy failed — highlight the link above to copy manually');
    }
  };

  return (
    <Modal onClose={onClose} title={created ? 'Client hub created' : 'Add client'}>
      {!created ? (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <Field label="First name" value={form.first_name} onChange={(v) => setForm({ ...form, first_name: v })} testId="dhub-first-name" required />
            <Field label="Last name" value={form.last_name} onChange={(v) => setForm({ ...form, last_name: v })} testId="dhub-last-name" required />
          </div>
          <Field label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testId="dhub-email" required />
          <Field label="Mobile" type="tel" value={form.mobile} onChange={(v) => setForm({ ...form, mobile: v })} testId="dhub-mobile" />
          <label className="block">
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-600">Starting stage</span>
            <select
              value={form.initial_stage}
              onChange={(e) => setForm({ ...form, initial_stage: e.target.value })}
              className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10"
              data-testid="dhub-initial-stage"
            >
              {stages.map((s) => <option key={s} value={s}>{stageLabels[s] || s}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-600">Initial notes (optional)</span>
            <textarea
              rows={2}
              value={form.initial_notes}
              onChange={(e) => setForm({ ...form, initial_notes: e.target.value })}
              className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10"
              data-testid="dhub-initial-notes"
            />
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="rounded-full border border-slate-300 text-slate-800 text-sm font-semibold px-4 py-2">Cancel</button>
            <button
              type="button"
              onClick={submit}
              disabled={busy}
              data-testid="dhub-create-submit"
              className={`rounded-full text-sm font-semibold px-4 py-2 ${busy ? 'bg-slate-200 text-slate-500' : 'bg-slate-900 hover:bg-slate-800 text-white'}`}
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create hub'}
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3">
            <p className="text-[11px] font-bold uppercase tracking-wider text-emerald-700">Hub created ✓</p>
            <p className="text-sm text-slate-800 mt-0.5">Send this personal link to the client — it stays valid for 12 months and can be regenerated any time.</p>
          </div>
          <div className="rounded-lg bg-slate-900 text-white p-3 text-[11px] font-mono break-all">
            {window.location.origin}/hub/{created.magic_token}
          </div>
          <button
            type="button"
            onClick={copyLink}
            data-testid="dhub-copy-link"
            className="w-full inline-flex items-center justify-center gap-1.5 rounded-full bg-slate-900 text-white text-sm font-semibold py-2.5"
          >
            <Copy className="w-4 h-4" /> Copy link
          </button>
          <button
            type="button"
            onClick={() => onCreated(created)}
            className="w-full rounded-full border border-slate-300 text-slate-800 text-sm font-semibold py-2.5"
          >
            Open client profile
          </button>
        </div>
      )}
    </Modal>
  );
};

// ─── Client detail drawer ───────────────────────────────────────────
const ClientDetailDrawer = ({ clientId, headers, stages, stageLabels, onClose, onChanged }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview'); // overview | journey | loan | documents | messages | requests | goals | idv | notes | audit

  const load = useCallback(async () => {
    try {
      const { data: d } = await axios.get(`${API}/api/hub/clients/${clientId}`, { headers });
      setData(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load');
    } finally { setLoading(false); }
  }, [clientId, headers]);

  useEffect(() => { load(); }, [load]);

  const patchClient = async (payload) => {
    try {
      await axios.patch(`${API}/api/hub/clients/${clientId}`, payload, { headers });
      toast.success('Saved');
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    }
  };

  const regenerateLink = async () => {
    try {
      const { data: r } = await axios.post(`${API}/api/hub/clients/${clientId}/regenerate-link`, {}, { headers });
      const url = `${window.location.origin}/hub/${r.magic_token}`;
      try {
        await navigator.clipboard.writeText(url);
        toast.success('New link generated & copied');
      } catch {
        toast.success(`New link: ${url}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    }
  };

  if (loading || !data) {
    return (
      <div className="fixed inset-0 z-40 bg-slate-900/40 backdrop-blur-sm flex items-stretch justify-end">
        <div className="bg-page w-full max-w-2xl overflow-y-auto shadow-2xl border-l border-slate-200 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      </div>
    );
  }

  const c = data.client;
  const updates = data.updates || [];
  const docs = data.documents || [];
  const requests = data.requests || [];

  return (
    <div className="fixed inset-0 z-40 bg-slate-900/40 backdrop-blur-sm flex items-stretch justify-end">
      <div className="bg-[var(--page-bg)] w-full max-w-2xl overflow-y-auto shadow-2xl border-l border-slate-200">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between z-10">
          <div className="min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-wider text-emerald-600">Client hub</p>
            <p className="text-lg font-black text-slate-900 truncate">{c.first_name} {c.last_name}</p>
            <p className="text-[11px] text-slate-500 truncate">{c.email}{c.mobile ? ` · ${c.mobile}` : ''}</p>
          </div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={regenerateLink} title="Regenerate link" className="rounded-full border border-slate-300 hover:bg-slate-100 text-xs font-semibold px-3 py-1.5 inline-flex items-center gap-1"><RefreshCw className="w-3.5 h-3.5" />Link</button>
            <button type="button" onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center"><X className="w-4 h-4" /></button>
          </div>
        </div>

        {/* Tabs */}
        <div className="sticky top-[73px] bg-white border-b border-slate-100 px-2 z-10 flex overflow-x-auto">
          {[
            { key: 'overview', label: 'Overview', icon: LayoutDashboard },
            { key: 'journey', label: 'Application', icon: TrendingUp },
            { key: 'loan', label: 'Loan', icon: Building2 },
            { key: 'documents', label: 'Documents', icon: FileText, count: docs.length },
            { key: 'messages', label: 'Messages', icon: MessageSquare, count: updates.length },
            { key: 'requests', label: 'Requests', icon: Sparkles, count: requests.length },
            { key: 'idv', label: 'ID Verify', icon: ShieldCheck },
            { key: 'goals', label: 'Goals', icon: Target },
            { key: 'post_settle', label: 'Post-settle', icon: Gem },
            { key: 'notes', label: 'Notes', icon: StickyNote },
            { key: 'audit', label: 'Audit log', icon: History },
          ].map(({ key, label, icon: Icon, count }) => (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              data-testid={`dhub-tab-${key}`}
              className={`whitespace-nowrap px-3 py-2.5 text-xs font-semibold flex items-center gap-1.5 border-b-2 transition-colors ${
                tab === key ? 'border-slate-900 text-slate-900' : 'border-transparent text-slate-500 hover:text-slate-800'
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              {label}
              {count > 0 && <span className="ml-1 min-w-[16px] h-4 px-1 rounded-full bg-slate-100 text-slate-700 text-[9px] font-bold flex items-center justify-center">{count}</span>}
            </button>
          ))}
        </div>

        <div className="p-4 space-y-4">
          {tab === 'overview' && (
            <OverviewPanel client={c} updates={updates} docs={docs} requests={requests} stages={stages} stageLabels={stageLabels} onGoTab={setTab} onPatch={patchClient} />
          )}
          {tab === 'journey' && (
            <JourneyPanel client={c} updates={updates} stages={stages} stageLabels={stageLabels} onPatch={patchClient} headers={headers} clientId={clientId} refresh={load} />
          )}
          {tab === 'loan' && <LoanPanel loan={c.loan || {}} onSave={(loan) => patchClient({ loan })} />}
          {tab === 'documents' && <DocumentsPanel docs={docs} clientId={clientId} headers={headers} refresh={load} />}
          {tab === 'messages' && <MessagesPanel updates={updates} clientId={clientId} headers={headers} refresh={load} />}
          {tab === 'requests' && <RequestsPanel requests={requests} headers={headers} refresh={load} />}
          {tab === 'idv' && <IdVerifyPanel idv={c.id_verification} client={c} />}
          {tab === 'goals' && <GoalsPanel goals={c.goals || {}} onSave={(goals) => patchClient({ goals })} />}
          {tab === 'post_settle' && <PostSettlePanel client={c} clientId={clientId} headers={headers} refresh={load} />}
          {tab === 'notes' && <NotesPanel clientId={clientId} headers={headers} />}
          {tab === 'audit' && <AuditLogPanel clientId={clientId} headers={headers} />}
        </div>
      </div>
    </div>
  );
};

// ─── Journey panel ─────────────────────────────────────────────────
const JourneyPanel = ({ client, updates, stages, stageLabels, onPatch, headers, clientId, refresh }) => {
  const [stage, setStage] = useState(client.stage);
  const [stageNotes, setStageNotes] = useState(client.stage_notes || '');
  const [nextAction, setNextAction] = useState(client.next_action_for_customer || '');
  const [bankEta, setBankEta] = useState(client.expected_bank_response || '');
  const [brokerEta, setBrokerEta] = useState(client.expected_broker_update ? client.expected_broker_update.slice(0, 16) : '');

  const save = () => onPatch({
    stage,
    stage_notes: stageNotes,
    next_action_for_customer: nextAction || null,
    expected_bank_response: bankEta || null,
    expected_broker_update: brokerEta || null,
  });

  return (
    <>
      <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3">
        <p className="text-[11px] font-bold uppercase tracking-wider text-slate-600">Current stage</p>
        <select
          value={stage}
          onChange={(e) => setStage(e.target.value)}
          data-testid="dhub-stage-select"
          className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10"
        >
          {stages.map((s) => <option key={s} value={s}>{stageLabels[s] || s}</option>)}
        </select>
        <label className="block">
          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Stage notes (visible to customer)</span>
          <textarea
            rows={2} value={stageNotes} onChange={(e) => setStageNotes(e.target.value)}
            data-testid="dhub-stage-notes"
            className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10"
          />
        </label>
        <label className="block">
          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">What the customer needs to do next</span>
          <input
            type="text" value={nextAction} onChange={(e) => setNextAction(e.target.value)}
            data-testid="dhub-next-action"
            className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10"
          />
        </label>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Bank response ETA" type="date" value={bankEta ? bankEta.slice(0, 10) : ''} onChange={setBankEta} testId="dhub-bank-eta" />
          <Field label="Broker update ETA" type="datetime-local" value={brokerEta} onChange={setBrokerEta} testId="dhub-broker-eta" />
        </div>
        <button
          type="button"
          onClick={save}
          data-testid="dhub-journey-save"
          className="w-full rounded-full bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold py-2.5"
        >
          Save & post update
        </button>
      </div>
      <MessagesPanel updates={updates} clientId={clientId} headers={headers} refresh={refresh} compact />
    </>
  );
};

// ─── Loan panel ────────────────────────────────────────────────────
const LoanPanel = ({ loan, onSave }) => {
  const [form, setForm] = useState({
    lender: loan.lender || '',
    amount: loan.amount || '',
    approved_balance: loan.approved_balance || '',
    current_balance: loan.current_balance || '',
    property_address: loan.property_address || '',
    purpose: loan.purpose || '',
    repayment_type: loan.repayment_type || '',
    interest_type: loan.interest_type || '',
    product: loan.product || '',
    rate: loan.rate || '',
    repayment_amount: loan.repayment_amount || '',
    offset_details: loan.offset_details || '',
    redraw_details: loan.redraw_details || '',
    approval_date: loan.approval_date || '',
    settlement_date: loan.settlement_date || '',
    fixed_expiry: loan.fixed_expiry || '',
    interest_only_expiry: loan.interest_only_expiry || '',
    broker_notes: loan.broker_notes || '',
    features: (loan.features || []).join(', '),
  });
  const save = () => {
    const payload = { ...form };
    payload.features = form.features.split(',').map((s) => s.trim()).filter(Boolean);
    onSave(payload);
  };
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3">
      <p className="text-[11px] font-bold uppercase tracking-wider text-slate-600">Loan summary</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <Field label="Lender" value={form.lender} onChange={(v) => setForm({ ...form, lender: v })} testId="dhub-loan-lender" />
        <Field label="Loan amount ($)" value={form.amount} onChange={(v) => setForm({ ...form, amount: v })} testId="dhub-loan-amount" />
        <Field label="Balance at approval ($)" value={form.approved_balance} onChange={(v) => setForm({ ...form, approved_balance: v })} testId="dhub-loan-approved-balance" />
        <Field label="Current balance ($)" value={form.current_balance} onChange={(v) => setForm({ ...form, current_balance: v })} testId="dhub-loan-current-balance" />
        <Field label="Property address" value={form.property_address} onChange={(v) => setForm({ ...form, property_address: v })} testId="dhub-loan-address" className="sm:col-span-2" />
        <Field label="Purpose" value={form.purpose} onChange={(v) => setForm({ ...form, purpose: v })} testId="dhub-loan-purpose" />
        <Field label="Product" value={form.product} onChange={(v) => setForm({ ...form, product: v })} testId="dhub-loan-product" />
        <Field label="Repayment type" value={form.repayment_type} onChange={(v) => setForm({ ...form, repayment_type: v })} testId="dhub-loan-reptype" />
        <Field label="Interest type" value={form.interest_type} onChange={(v) => setForm({ ...form, interest_type: v })} testId="dhub-loan-inttype" />
        <Field label="Interest rate (%)" value={form.rate} onChange={(v) => setForm({ ...form, rate: v })} testId="dhub-loan-rate" />
        <Field label="Repayment amount ($)" value={form.repayment_amount} onChange={(v) => setForm({ ...form, repayment_amount: v })} testId="dhub-loan-repayment" />
        <Field label="Offset details" value={form.offset_details} onChange={(v) => setForm({ ...form, offset_details: v })} testId="dhub-loan-offset" />
        <Field label="Redraw details" value={form.redraw_details} onChange={(v) => setForm({ ...form, redraw_details: v })} testId="dhub-loan-redraw" />
        <Field label="Approval date" type="date" value={form.approval_date} onChange={(v) => setForm({ ...form, approval_date: v })} testId="dhub-loan-approval" />
        <Field label="Settlement date" type="date" value={form.settlement_date} onChange={(v) => setForm({ ...form, settlement_date: v })} testId="dhub-loan-settlement" />
        <Field label="Fixed rate expiry" type="date" value={form.fixed_expiry} onChange={(v) => setForm({ ...form, fixed_expiry: v })} testId="dhub-loan-fixed" />
        <Field label="Interest-only expiry" type="date" value={form.interest_only_expiry} onChange={(v) => setForm({ ...form, interest_only_expiry: v })} testId="dhub-loan-io" />
      </div>
      <Field label="Features (comma separated)" value={form.features} onChange={(v) => setForm({ ...form, features: v })} testId="dhub-loan-features" />
      <label className="block">
        <span className="text-[11px] font-bold uppercase tracking-wider text-slate-600">Broker notes to customer</span>
        <textarea
          rows={3} value={form.broker_notes} onChange={(e) => setForm({ ...form, broker_notes: e.target.value })}
          data-testid="dhub-loan-notes"
          className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10"
        />
      </label>
      <button type="button" onClick={save} data-testid="dhub-loan-save" className="w-full rounded-full bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold py-2.5">Save loan details</button>
    </div>
  );
};

// ─── Documents panel ───────────────────────────────────────────────
const DocumentsPanel = ({ docs, clientId, headers, refresh }) => {
  const [openNew, setOpenNew] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', due_date: '' });

  const createDoc = async () => {
    if (!form.name.trim()) { toast.error('Document name required'); return; }
    try {
      await axios.post(`${API}/api/hub/clients/${clientId}/documents`, form, { headers });
      setOpenNew(false); setForm({ name: '', description: '', due_date: '' });
      toast.success('Document requested');
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const updateDoc = async (docId, patch) => {
    try {
      await axios.patch(`${API}/api/hub/documents/${docId}`, patch, { headers });
      toast.success('Updated');
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  return (
    <>
      <div className="flex items-center justify-between">
        <p className="text-[11px] font-bold uppercase tracking-wider text-slate-600">Document requests</p>
        <button type="button" onClick={() => setOpenNew(!openNew)} data-testid="dhub-add-doc" className="text-xs font-semibold text-slate-900 hover:underline inline-flex items-center gap-1">
          <Plus className="w-3.5 h-3.5" /> Request a document
        </button>
      </div>
      {openNew && (
        <div className="rounded-2xl border border-slate-200 bg-white p-3 space-y-2">
          <Field label="Document name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testId="dhub-doc-name" required />
          <label className="block">
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-600">Description (optional)</span>
            <textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" data-testid="dhub-doc-desc" />
          </label>
          <Field label="Due date (optional)" type="date" value={form.due_date} onChange={(v) => setForm({ ...form, due_date: v })} testId="dhub-doc-due" />
          <div className="grid grid-cols-2 gap-2">
            <button type="button" onClick={() => setOpenNew(false)} className="w-full rounded-full border border-slate-300 text-slate-800 text-xs font-semibold py-2">Cancel</button>
            <button type="button" onClick={createDoc} data-testid="dhub-doc-create" className="w-full rounded-full bg-slate-900 text-white text-xs font-semibold py-2">Send request</button>
          </div>
        </div>
      )}
      {docs.length === 0 && <p className="text-xs text-slate-500 text-center py-6">No documents requested yet.</p>}
      {docs.map((d) => <DocRowAdmin key={d.id} doc={d} onUpdate={updateDoc} headers={headers} />)}
    </>
  );
};

const DocRowAdmin = ({ doc, onUpdate, headers }) => {
  const [notes, setNotes] = useState(doc.broker_notes || '');
  const download = async () => {
    try {
      const resp = await axios.get(`${API}/api/hub/documents/${doc.id}/file`, { headers, responseType: 'blob' });
      const url = URL.createObjectURL(resp.data);
      window.open(url, '_blank');
    } catch (e) { toast.error('Download failed'); }
  };
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-bold text-slate-900">{doc.name}</p>
          {doc.description && <p className="text-xs text-slate-600 mt-0.5">{doc.description}</p>}
          <p className="text-[10px] text-slate-400 mt-1">Status: <b>{doc.status.replace(/_/g, ' ')}</b>{doc.due_date ? ` · Due ${fmtDate(doc.due_date)}` : ''}</p>
        </div>
        {doc.file_ref && (
          <button type="button" onClick={download} className="rounded-full border border-slate-300 hover:bg-slate-100 text-[11px] font-semibold px-2 py-1 inline-flex items-center gap-1">
            <ArrowRight className="w-3 h-3" /> View
          </button>
        )}
      </div>
      {doc.sent_via_email_note && (
        <p className="text-[11px] text-purple-800 mt-2 rounded bg-purple-50 border border-purple-200 p-2">Customer note: {doc.sent_via_email_note}</p>
      )}
      <div className="mt-2 flex flex-wrap gap-1.5">
        {['reviewed', 'approved', 'needs_correction'].map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => onUpdate(doc.id, { status: s, broker_notes: notes })}
            className="rounded-full text-[10px] font-semibold px-2 py-1 border border-slate-300 hover:bg-slate-100 capitalize"
          >
            {s.replace('_', ' ')}
          </button>
        ))}
      </div>
      <input
        type="text" value={notes} onChange={(e) => setNotes(e.target.value)}
        placeholder="Optional broker note when marking status…"
        className="mt-2 w-full px-3 py-1.5 rounded-lg border border-slate-200 text-xs"
      />
    </div>
  );
};

// ─── Messages panel ────────────────────────────────────────────────
const MessagesPanel = ({ updates, clientId, headers, refresh, compact = false }) => {
  const [text, setText] = useState('');
  const post = async () => {
    if (!text.trim()) return;
    try {
      await axios.post(`${API}/api/hub/clients/${clientId}/updates`, {
        category: 'message', title: 'Update from your broker', message: text.trim(),
      }, { headers });
      setText('');
      refresh();
    } catch (e) { toast.error('Failed'); }
  };
  return (
    <>
      <div className="rounded-2xl border border-slate-200 bg-white p-3">
        <p className="text-[11px] font-bold uppercase tracking-wider text-slate-600 mb-2">Post an update</p>
        <textarea
          rows={2} value={text} onChange={(e) => setText(e.target.value)}
          placeholder="Broadcast a message or update to the client…"
          data-testid="dhub-post-update"
          className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm"
        />
        <button type="button" onClick={post} disabled={!text.trim()} data-testid="dhub-post-update-btn" className={`mt-2 w-full inline-flex items-center justify-center gap-1.5 rounded-full text-sm font-semibold py-2 ${text.trim() ? 'bg-slate-900 text-white' : 'bg-slate-200 text-slate-500'}`}>
          <Send className="w-3.5 h-3.5" /> Post update
        </button>
      </div>
      {!compact && (
        <div className="space-y-2">
          {updates.length === 0 && <p className="text-xs text-slate-500 text-center py-6">No activity yet.</p>}
          {updates.map((u) => (
            <div key={u.id} className={`rounded-xl p-3 border ${u.posted_by_role === 'customer' ? 'bg-emerald-50 border-emerald-200' : u.posted_by_role === 'broker' ? 'bg-white border-slate-200' : 'bg-slate-50 border-slate-200'}`}>
              <p className="text-xs font-bold text-slate-900">{u.title}</p>
              {u.message && <p className="text-xs text-slate-700 mt-0.5">{u.message}</p>}
              <p className="text-[10px] text-slate-400 mt-1">{u.posted_by_name} · {fmtDateTime(u.posted_at)}</p>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

// ─── Requests panel ────────────────────────────────────────────────
const RequestsPanel = ({ requests, headers, refresh }) => {
  const [openIds, setOpenIds] = useState({});
  const [replies, setReplies] = useState({});
  const respond = async (reqId, status = 'acknowledged') => {
    try {
      await axios.patch(`${API}/api/hub/requests/${reqId}`, { status, broker_response: replies[reqId] || null }, { headers });
      toast.success('Response saved');
      refresh();
    } catch (e) { toast.error('Failed'); }
  };
  if (requests.length === 0) return <p className="text-xs text-slate-500 text-center py-6">No client requests yet.</p>;
  return (
    <div className="space-y-2">
      {requests.map((r) => (
        <div key={r.id} className={`rounded-xl border p-3 ${r.status === 'new' ? 'bg-amber-50 border-amber-200' : 'bg-white border-slate-200'}`}>
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="text-sm font-bold text-slate-900">{r.request_type_label}</p>
              <p className="text-[11px] text-slate-500">{fmtDateTime(r.created_at)} · Urgency: <b>{r.urgency}</b></p>
            </div>
            <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full ${
              r.status === 'new' ? 'bg-amber-200 text-amber-900' : r.status === 'resolved' ? 'bg-emerald-200 text-emerald-900' : 'bg-slate-200 text-slate-800'
            }`}>{r.status}</span>
          </div>
          <p className="text-xs text-slate-800 mt-2 whitespace-pre-line">{r.message}</p>
          {r.attachments && r.attachments.length > 0 && (
            <div className="mt-2 space-y-1">
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Attachments ({r.attachments.length})</p>
              {r.attachments.map((att, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={async () => {
                    try {
                      const resp = await axios.get(`${API}/api/hub/requests/${r.id}/attachments/${idx}`, { headers, responseType: 'blob' });
                      const url = URL.createObjectURL(resp.data);
                      window.open(url, '_blank');
                    } catch { toast.error('Download failed'); }
                  }}
                  className="w-full flex items-center justify-between rounded bg-slate-50 border border-slate-200 hover:bg-slate-100 px-2 py-1.5 text-[11px] text-left"
                >
                  <span className="truncate flex-1 min-w-0 mr-2">
                    <FileText className="w-3 h-3 inline mr-1" />
                    {att.original_filename || `Attachment ${idx + 1}`}
                    <span className="text-slate-400 ml-1">· {Math.round((att.size || 0) / 1024)} KB</span>
                  </span>
                  <ArrowRight className="w-3 h-3 text-slate-500 flex-shrink-0" />
                </button>
              ))}
            </div>
          )}
          {r.broker_response && (
            <div className="mt-2 rounded bg-emerald-50 border border-emerald-200 p-2">
              <p className="text-[10px] font-bold uppercase text-emerald-700">Your reply</p>
              <p className="text-xs text-slate-800">{r.broker_response}</p>
            </div>
          )}
          <div className="mt-2">
            {!openIds[r.id] ? (
              <button type="button" onClick={() => setOpenIds({ ...openIds, [r.id]: true })} className="text-xs font-semibold text-slate-900 hover:underline">Reply</button>
            ) : (
              <div className="space-y-2">
                <textarea rows={2} value={replies[r.id] || ''} onChange={(e) => setReplies({ ...replies, [r.id]: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-xs" placeholder="Reply to the client…" />
                <div className="grid grid-cols-3 gap-1.5">
                  <button type="button" onClick={() => respond(r.id, 'acknowledged')} className="rounded-full border border-slate-300 text-xs font-semibold py-1.5">Acknowledge</button>
                  <button type="button" onClick={() => respond(r.id, 'resolved')} className="rounded-full bg-emerald-600 text-white text-xs font-semibold py-1.5">Resolve</button>
                  <button type="button" onClick={() => setOpenIds({ ...openIds, [r.id]: false })} className="rounded-full border border-slate-300 text-xs font-semibold py-1.5">Close</button>
                </div>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

// ─── Goals panel ───────────────────────────────────────────────────
const GoalsPanel = ({ goals, onSave }) => {
  const fields = [
    ['short_term', 'Short-term goals'],
    ['long_term', 'Long-term goals'],
    ['property', 'Property goals'],
    ['debt_reduction', 'Debt reduction goals'],
    ['refinance', 'Refinance goals'],
    ['investment', 'Investment goals'],
    ['repayment_strategy', 'Repayment strategy'],
    ['action_plan', 'Action plan'],
    ['broker_notes', 'Notes from broker'],
  ];
  const [form, setForm] = useState(() => Object.fromEntries(fields.map(([k]) => [k, goals[k] || ''])));
  const [reviewDate, setReviewDate] = useState(goals.review_date || '');
  const save = () => onSave({ ...form, review_date: reviewDate || null });
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3">
      <p className="text-[11px] font-bold uppercase tracking-wider text-slate-600">Client goals</p>
      {fields.map(([k, label]) => (
        <label key={k} className="block">
          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</span>
          <textarea
            rows={2} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
            data-testid={`dhub-goals-${k}`}
            className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm"
          />
        </label>
      ))}
      <Field label="Next review date" type="date" value={reviewDate} onChange={setReviewDate} testId="dhub-goals-review-date" />
      <button type="button" onClick={save} data-testid="dhub-goals-save" className="w-full rounded-full bg-slate-900 text-white text-sm font-semibold py-2.5">Save goals</button>
    </div>
  );
};

// ─── Shared primitives ─────────────────────────────────────────────
const Field = ({ label, value, onChange, type = 'text', placeholder, required, testId, className = '' }) => (
  <label className={`block ${className}`}>
    <span className="text-[11px] font-bold uppercase tracking-wider text-slate-600">
      {label}{required && <span className="text-red-500 ml-0.5">*</span>}
    </span>
    <input
      type={type}
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      data-testid={testId}
      className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-900/10"
    />
  </label>
);

const Modal = ({ title, children, onClose }) => (
  <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4">
    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
      <div className="sticky top-0 bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between">
        <h3 className="text-base font-black text-slate-900">{title}</h3>
        <button type="button" onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center"><X className="w-4 h-4" /></button>
      </div>
      <div className="p-4">{children}</div>
    </div>
  </div>
);



// ─── Command Centre (broker dashboard) ─────────────────────────────
const CommandCentre = ({ headers, onDrillDown, onNewClient }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/hub/dashboard/stats`, { headers });
        setStats(data);
      } catch (e) { toast.error('Failed to load dashboard'); }
      finally { setLoading(false); }
    })();
  }, [headers]);

  if (loading) return <div className="flex items-center justify-center py-16 text-slate-500"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading dashboard…</div>;
  if (!stats) return null;

  const cards = [
    { key: 'docs_review', label: 'Documents waiting for review', value: stats.docs_review, icon: FileText, tone: 'sky', drill: 'docs_review' },
    { key: 'updates_needing_reply', label: 'Customer updates needing reply', value: stats.updates_needing_reply, icon: MessageSquare, tone: 'amber', drill: null, hint: 'Client messages posted in the last 7 days' },
    { key: 'stale_apps', label: 'No update in over 48 hours', value: stats.stale_apps, icon: Clock, tone: 'red', drill: 'stale' },
    { key: 'bank_eta_today', label: 'Bank response ETA due today', value: stats.bank_eta_due_today, icon: Building2, tone: 'purple', drill: 'bank_eta_today' },
    { key: 'idv_incomplete', label: 'ID verifications incomplete', value: stats.idv_incomplete, icon: ShieldCheck, tone: 'amber', drill: 'idv_incomplete' },
    { key: 'settlements_upcoming', label: 'Settlements in next 14 days', value: stats.settlements_upcoming, icon: HomeIcon, tone: 'emerald', drill: 'settlements_upcoming' },
    { key: 'urgent_requests', label: 'Urgent client requests', value: stats.urgent_requests, icon: AlertTriangle, tone: 'red', drill: 'new_requests' },
    { key: 'post_settle_reviews_due', label: 'Post-settlement reviews due', value: stats.post_settle_reviews_due, icon: TrendingUp, tone: 'slate', drill: 'post_settle' },
    { key: 'portal_activity_today', label: 'Portal activity today', value: stats.portal_activity_today, icon: Zap, tone: 'sky', drill: null, hint: 'Total updates across all your clients since midnight' },
    { key: 'total_clients', label: 'Total active clients', value: stats.total_clients, icon: User, tone: 'slate', drill: null, hint: 'Includes settled & post-settlement' },
  ];

  const toneClass = {
    red: 'bg-red-50 border-red-200 text-red-800',
    amber: 'bg-amber-50 border-amber-200 text-amber-800',
    sky: 'bg-sky-50 border-sky-200 text-sky-800',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    purple: 'bg-purple-50 border-purple-200 text-purple-800',
    slate: 'bg-slate-50 border-slate-200 text-slate-800',
  };

  return (
    <div>
      <div className="mb-4">
        <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Your day at a glance</p>
        <p className="text-sm text-slate-600 mt-0.5">Click a card to drill into the clients that need attention.</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
        {cards.map((c) => {
          const clickable = c.drill != null;
          const Icon = c.icon;
          return (
            <button
              key={c.key}
              type="button"
              onClick={() => clickable && onDrillDown(c.drill)}
              disabled={!clickable}
              data-testid={`dhub-cc-${c.key}`}
              className={`text-left rounded-2xl border p-4 transition-all ${
                clickable ? `${toneClass[c.tone]} hover:shadow-md hover:-translate-y-0.5 cursor-pointer` : 'bg-white border-slate-200 cursor-default'
              }`}
              title={c.hint || ''}
            >
              <div className="flex items-start justify-between mb-2">
                <Icon className={`w-5 h-5 ${clickable ? '' : 'text-slate-400'}`} />
                {clickable && c.value > 0 && <ArrowRight className="w-3.5 h-3.5 opacity-60" />}
              </div>
              <p className="text-3xl font-black text-slate-900">{c.value}</p>
              <p className="text-[11px] font-semibold text-slate-700 mt-1 leading-tight">{c.label}</p>
            </button>
          );
        })}
      </div>

      {/* Quick actions */}
      <div className="rounded-2xl bg-slate-900 text-white p-5">
        <p className="text-[11px] font-bold uppercase tracking-wider text-emerald-300 mb-2">Quick actions</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <button type="button" onClick={onNewClient} className="rounded-full bg-white text-slate-900 hover:bg-slate-100 text-xs font-semibold px-3 py-2 inline-flex items-center justify-center gap-1"><Plus className="w-3.5 h-3.5" /> New client</button>
          <button type="button" onClick={() => onDrillDown('docs_review')} className="rounded-full bg-white/10 hover:bg-white/20 text-white text-xs font-semibold px-3 py-2 inline-flex items-center justify-center gap-1"><FileText className="w-3.5 h-3.5" /> Review docs</button>
          <button type="button" onClick={() => onDrillDown('urgent')} className="rounded-full bg-white/10 hover:bg-white/20 text-white text-xs font-semibold px-3 py-2 inline-flex items-center justify-center gap-1"><Flag className="w-3.5 h-3.5" /> Urgent</button>
          <button type="button" onClick={() => onDrillDown('stale')} className="rounded-full bg-white/10 hover:bg-white/20 text-white text-xs font-semibold px-3 py-2 inline-flex items-center justify-center gap-1"><Clock className="w-3.5 h-3.5" /> Stale apps</button>
        </div>
      </div>
    </div>
  );
};

// ─── Client Overview panel (drawer tab 1) ──────────────────────────
const OverviewPanel = ({ client, updates, docs, requests, onGoTab, onPatch }) => {
  const pendingDocs = docs.filter((d) => d.status === 'requested' || d.status === 'needs_correction').length;
  const awaitingReview = docs.filter((d) => d.status === 'uploaded' || d.status === 'sent_via_email').length;
  const newReqs = requests.filter((r) => r.status === 'new').length;
  const latestCustomerUpdate = updates.find((u) => u.posted_by_role === 'customer');

  const togglePriority = () => {
    const next = client.priority === 'high' ? 'medium' : 'high';
    onPatch({ priority: next });
  };
  const toggleUrgent = () => onPatch({ is_urgent: !client.is_urgent });

  return (
    <>
      <div className="rounded-2xl bg-white border border-slate-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Snapshot</p>
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={togglePriority}
              data-testid="dhub-overview-priority"
              className={`rounded-full text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 border ${
                client.priority === 'high' ? 'bg-red-500 border-red-500 text-white' : 'bg-white border-slate-300 text-slate-700 hover:bg-slate-50'
              }`}
            >
              <Flag className="w-3 h-3 inline mr-1" /> {client.priority || 'medium'}
            </button>
            <button
              type="button"
              onClick={toggleUrgent}
              data-testid="dhub-overview-urgent"
              className={`rounded-full text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 border ${
                client.is_urgent ? 'bg-red-100 border-red-300 text-red-800' : 'bg-white border-slate-300 text-slate-600 hover:bg-slate-50'
              }`}
            >
              <AlertTriangle className="w-3 h-3 inline mr-1" /> {client.is_urgent ? 'Urgent' : 'Not urgent'}
            </button>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <SnapItem label="Stage" value={client.stage_label} />
          <SnapItem label="Lender" value={client.loan?.lender || '—'} />
          <SnapItem label="Loan amount" value={client.loan?.amount ? `$${Number(client.loan.amount).toLocaleString()}` : '—'} />
          <SnapItem label="Property" value={client.loan?.property_address || '—'} />
          <SnapItem label="Bank ETA" value={fmtDate(client.expected_bank_response)} />
          <SnapItem label="Broker update" value={fmtDate(client.expected_broker_update)} />
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <OverviewChip label="Docs due" value={pendingDocs} tone="amber" onClick={() => onGoTab('documents')} />
        <OverviewChip label="To review" value={awaitingReview} tone="sky" onClick={() => onGoTab('documents')} />
        <OverviewChip label="New requests" value={newReqs} tone="purple" onClick={() => onGoTab('requests')} />
        <OverviewChip label="Activity" value={updates.length} tone="slate" onClick={() => onGoTab('messages')} />
      </div>

      {latestCustomerUpdate && (
        <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-4">
          <p className="text-[10px] font-bold uppercase tracking-wider text-emerald-700">Latest customer update</p>
          <p className="text-sm text-slate-900 mt-1 font-semibold">{latestCustomerUpdate.title}</p>
          {latestCustomerUpdate.message && <p className="text-xs text-slate-700 mt-1 whitespace-pre-line">{latestCustomerUpdate.message}</p>}
          <p className="text-[10px] text-slate-500 mt-1">{fmtDateTime(latestCustomerUpdate.posted_at)}</p>
          <button type="button" onClick={() => onGoTab('messages')} className="mt-2 text-xs font-semibold text-emerald-800 hover:underline">Open messages →</button>
        </div>
      )}

      <div className="grid grid-cols-2 gap-2">
        <button type="button" onClick={() => onGoTab('journey')} className="rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold py-2.5 inline-flex items-center justify-center gap-1.5"><TrendingUp className="w-3.5 h-3.5" /> Update stage</button>
        <button type="button" onClick={() => onGoTab('documents')} className="rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold py-2.5 inline-flex items-center justify-center gap-1.5"><Plus className="w-3.5 h-3.5" /> Request document</button>
      </div>
    </>
  );
};

const SnapItem = ({ label, value }) => (
  <div>
    <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">{label}</p>
    <p className="text-sm font-semibold text-slate-900 mt-0.5 break-words">{value || '—'}</p>
  </div>
);
const OverviewChip = ({ label, value, tone, onClick }) => {
  const toneCls = { amber: 'bg-amber-100 text-amber-800', sky: 'bg-sky-100 text-sky-800', purple: 'bg-purple-100 text-purple-800', slate: 'bg-slate-100 text-slate-800' }[tone] || 'bg-slate-100 text-slate-800';
  return (
    <button type="button" onClick={onClick} className={`rounded-xl ${toneCls} px-3 py-2 text-left hover:opacity-90 transition-opacity`}>
      <p className="text-2xl font-black">{value}</p>
      <p className="text-[10px] uppercase tracking-wider font-bold">{label}</p>
    </button>
  );
};

// ─── ID Verification panel ─────────────────────────────────────────
const IdVerifyPanel = ({ idv, client }) => (
  <div className="rounded-2xl bg-white border border-slate-200 p-4">
    <div className="flex items-center gap-2 mb-3">
      <ShieldCheck className="w-4 h-4 text-slate-500" />
      <p className="text-[11px] font-bold uppercase tracking-wider text-slate-600">ID verification</p>
    </div>
    {!idv ? (
      <div className="text-center py-6">
        <p className="text-sm text-slate-600">No ID verification linked yet.</p>
        <p className="text-xs text-slate-400 mt-1">Create one in the ID Verification Portal, then paste the reference into the client&apos;s profile.</p>
        <a href="/id-verification" className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold px-3 py-2">
          <ShieldCheck className="w-3.5 h-3.5" /> Open ID Verification Portal
        </a>
      </div>
    ) : (
      <div className="space-y-2">
        <SnapItem label="Status" value={idv.status_label} />
        <SnapItem label="Reference" value={idv.reference_number} />
        <SnapItem label="Submitted" value={fmtDateTime(idv.submitted_at)} />
        <SnapItem label="Report generated" value={fmtDateTime(idv.report_generated_at)} />
        {idv.has_report && (
          <a
            href={`/api/id-verify/verifications/${idv.vid}/report`}
            target="_blank"
            rel="noreferrer"
            className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold px-3 py-2"
          >
            <FileText className="w-3.5 h-3.5" /> Download PDF report
          </a>
        )}
      </div>
    )}
  </div>
);

// ─── Post-settlement retention panel — broker sets valuation & rate-watch ─
const PostSettlePanel = ({ client, clientId, headers, refresh }) => {
  const pv = client.property_valuation || {};
  const rw = client.rate_watch || {};
  const anniv = client.anniversary_review;

  const [pvValue, setPvValue] = useState(pv.current_value || '');
  const [pvDate, setPvDate] = useState(pv.valuation_date || new Date().toISOString().slice(0, 10));
  const [pvSource, setPvSource] = useState(pv.source || 'manual');
  const [pvNotes, setPvNotes] = useState(pv.notes || '');
  const [pvBusy, setPvBusy] = useState(false);

  const [rwCurrent, setRwCurrent] = useState(rw.current_rate ?? '');
  const [rwCurrentLender, setRwCurrentLender] = useState(rw.current_lender || client.loan?.lender || '');
  const [rwBest, setRwBest] = useState(rw.market_best_rate ?? '');
  const [rwBestLender, setRwBestLender] = useState(rw.market_best_lender || '');
  const [rwNotes, setRwNotes] = useState(rw.notes || '');
  const [rwBusy, setRwBusy] = useState(false);

  const [annBusy, setAnnBusy] = useState(false);

  const saveValuation = async () => {
    if (!pvValue || parseFloat(pvValue) <= 0) { toast.error('Enter a valuation amount'); return; }
    setPvBusy(true);
    try {
      await axios.patch(`${API}/api/hub/clients/${clientId}/property-valuation`, {
        current_value: parseFloat(pvValue),
        valuation_date: pvDate,
        source: pvSource,
        notes: pvNotes.trim() || null,
      }, { headers });
      toast.success('Property valuation updated — client will see it on their hub');
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setPvBusy(false); }
  };

  const saveRateWatch = async () => {
    if (!rwCurrent || !rwBest) { toast.error('Enter both the current and market-best rate'); return; }
    setRwBusy(true);
    try {
      await axios.patch(`${API}/api/hub/clients/${clientId}/rate-watch`, {
        current_rate: parseFloat(rwCurrent),
        current_lender: rwCurrentLender.trim() || null,
        market_best_rate: parseFloat(rwBest),
        market_best_lender: rwBestLender.trim() || null,
        notes: rwNotes.trim() || null,
      }, { headers });
      toast.success('Rate watch refreshed — client will see it on their hub');
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setRwBusy(false); }
  };

  const markReviewDone = async () => {
    setAnnBusy(true);
    try {
      await axios.post(`${API}/api/hub/clients/${clientId}/mark-annual-review-done`, {}, { headers });
      toast.success('Annual review marked as done — 11-month clock reset');
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setAnnBusy(false); }
  };

  return (
    <>
      {/* Anniversary review status */}
      <div className="rounded-2xl border border-slate-200 bg-white p-4">
        <p className="text-[11px] font-bold uppercase tracking-wider text-slate-600 flex items-center gap-1.5 mb-3">
          <CalendarClock className="w-3.5 h-3.5" /> Annual review status
        </p>
        {!anniv && <p className="text-xs text-slate-500">Client hasn&apos;t settled yet — no annual review to track.</p>}
        {anniv && (
          <>
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Months since settlement</p>
                <p className="text-sm font-semibold text-slate-900 mt-0.5">{anniv.months_since_settlement}</p>
              </div>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Months since last review</p>
                <p className="text-sm font-semibold text-slate-900 mt-0.5">{anniv.months_since_last_review ?? '—'}</p>
              </div>
            </div>
            {anniv.review_due ? (
              <div className="mt-3 rounded-lg bg-amber-50 border border-amber-200 p-2.5">
                <p className="text-[10px] font-bold uppercase tracking-wider text-amber-700 mb-0.5">Review due</p>
                <p className="text-xs text-slate-800">The client&apos;s hub is currently showing the &quot;Book my annual review&quot; nudge.</p>
              </div>
            ) : (
              <p className="mt-3 text-xs text-emerald-700">On track — next review not due yet.</p>
            )}
            <button
              type="button" onClick={markReviewDone} disabled={annBusy}
              data-testid="dhub-mark-annual-review-done"
              className="mt-3 w-full inline-flex items-center justify-center gap-1.5 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold py-2 disabled:opacity-60"
            >
              {annBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
              Mark annual review completed
            </button>
          </>
        )}
      </div>

      {/* Property valuation */}
      <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3">
        <p className="text-[11px] font-bold uppercase tracking-wider text-slate-600 flex items-center gap-1.5">
          <HomeIcon className="w-3.5 h-3.5" /> Property valuation
        </p>
        <p className="text-[11px] text-slate-500 -mt-2">
          Later this will auto-refresh from CoreLogic/Domain. For now, set it manually — clients see the delta vs their purchase price on their hub.
        </p>
        <div className="grid grid-cols-2 gap-3">
          <label className="block col-span-2 sm:col-span-1">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Current value ($)</span>
            <input type="number" value={pvValue} onChange={(e) => setPvValue(e.target.value)}
                   data-testid="dhub-pv-value"
                   className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10" />
          </label>
          <label className="block col-span-2 sm:col-span-1">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Valuation date</span>
            <input type="date" value={pvDate} onChange={(e) => setPvDate(e.target.value)}
                   className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10" />
          </label>
          <label className="block col-span-2 sm:col-span-1">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Source</span>
            <select value={pvSource} onChange={(e) => setPvSource(e.target.value)}
                    className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm">
              <option value="manual">Manual</option>
              <option value="CoreLogic">CoreLogic</option>
              <option value="Domain">Domain</option>
            </select>
          </label>
          <label className="block col-span-2">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Notes to client (optional)</span>
            <textarea rows={2} value={pvNotes} onChange={(e) => setPvNotes(e.target.value)}
                      placeholder='e.g. "Recent comparable sale on your street for $1.18M"'
                      className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-slate-900/10" />
          </label>
        </div>
        <button type="button" onClick={saveValuation} disabled={pvBusy}
                data-testid="dhub-save-valuation"
                className="w-full inline-flex items-center justify-center gap-1.5 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold py-2 disabled:opacity-60">
          {pvBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          Save valuation
        </button>
        {pv.current_value && (
          <p className="text-[11px] text-slate-500">
            Last set: ${pv.current_value?.toLocaleString()} · {pv.source} · {pv.valuation_date}
          </p>
        )}
      </div>

      {/* Rate watch */}
      <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3">
        <p className="text-[11px] font-bold uppercase tracking-wider text-slate-600 flex items-center gap-1.5">
          <Percent className="w-3.5 h-3.5" /> Rate watch
        </p>
        <p className="text-[11px] text-slate-500 -mt-2">
          Compare the client&apos;s rate with the best comparable market rate. If the gap is &ge; 0.10%, the client sees a &quot;Rate alert&quot; card with a &quot;Book rate review&quot; CTA.
        </p>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Client&apos;s current rate (%)</span>
            <input type="number" step="0.01" value={rwCurrent} onChange={(e) => setRwCurrent(e.target.value)}
                   data-testid="dhub-rw-current"
                   className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10" />
          </label>
          <label className="block">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Market best rate (%)</span>
            <input type="number" step="0.01" value={rwBest} onChange={(e) => setRwBest(e.target.value)}
                   data-testid="dhub-rw-best"
                   className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10" />
          </label>
          <label className="block">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Current lender</span>
            <input type="text" value={rwCurrentLender} onChange={(e) => setRwCurrentLender(e.target.value)}
                   placeholder="e.g. CBA" className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10" />
          </label>
          <label className="block">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Market-best lender</span>
            <input type="text" value={rwBestLender} onChange={(e) => setRwBestLender(e.target.value)}
                   placeholder="e.g. Macquarie" className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10" />
          </label>
          <label className="block col-span-2">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Notes to client (optional)</span>
            <textarea rows={2} value={rwNotes} onChange={(e) => setRwNotes(e.target.value)}
                      placeholder='e.g. "Macquarie is running a re-fi promo until end of quarter"'
                      className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-slate-900/10" />
          </label>
        </div>
        <button type="button" onClick={saveRateWatch} disabled={rwBusy}
                data-testid="dhub-save-rate-watch"
                className="w-full inline-flex items-center justify-center gap-1.5 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold py-2 disabled:opacity-60">
          {rwBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          Save rate watch
        </button>
        {rw.current_rate != null && (
          <p className="text-[11px] text-slate-500">
            Last checked: {rw.current_rate}% vs {rw.market_best_rate}% · {rw.last_checked}
          </p>
        )}
      </div>
    </>
  );
};

// ─── Notes panel (broker-only) ─────────────────────────────────────
const NotesPanel = ({ clientId, headers }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/hub/clients/${clientId}/notes`, { headers });
      setItems(data.items || []);
    } catch (e) { toast.error('Failed to load notes'); }
    finally { setLoading(false); }
  }, [clientId, headers]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!editing?.body?.trim()) { toast.error('Note body required'); return; }
    try {
      const payload = { body: editing.body.trim(), category: editing.category || 'general', is_pinned: !!editing.is_pinned };
      if (editing.id) await axios.patch(`${API}/api/hub/notes/${editing.id}`, payload, { headers });
      else await axios.post(`${API}/api/hub/clients/${clientId}/notes`, payload, { headers });
      toast.success('Saved');
      setEditing(null);
      load();
    } catch { toast.error('Failed to save'); }
  };
  const remove = async (id) => {
    if (!window.confirm('Delete this note?')) return;
    try {
      await axios.delete(`${API}/api/hub/notes/${id}`, { headers });
      toast.success('Deleted');
      load();
    } catch { toast.error('Failed'); }
  };

  const catTone = {
    general: 'bg-slate-100 text-slate-700',
    credit: 'bg-purple-100 text-purple-800',
    strategy: 'bg-emerald-100 text-emerald-800',
    follow_up: 'bg-amber-100 text-amber-800',
    compliance: 'bg-red-100 text-red-800',
    post_settlement: 'bg-sky-100 text-sky-800',
  };

  return (
    <>
      <div className="rounded-2xl bg-amber-50 border border-amber-200 p-3 flex items-start gap-2">
        <StickyNote className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-amber-900">
          <b>Broker-only notes.</b> These never appear on the customer&apos;s portal.
        </p>
      </div>

      <div className="flex items-center justify-between">
        <p className="text-[11px] font-bold uppercase tracking-wider text-slate-600">Internal notes</p>
        <button
          type="button"
          onClick={() => setEditing({ body: '', category: 'general', is_pinned: false })}
          data-testid="dhub-note-new"
          className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold px-3 py-2"
        >
          <Plus className="w-3.5 h-3.5" /> New note
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8 text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /></div>
      ) : items.length === 0 ? (
        <p className="text-xs text-slate-500 text-center py-6">No internal notes yet.</p>
      ) : items.map((n) => (
        <div key={n.id} className={`rounded-2xl border p-3 ${n.is_pinned ? 'bg-amber-50 border-amber-200' : 'bg-white border-slate-200'}`} data-testid={`dhub-note-${n.id}`}>
          <div className="flex items-start justify-between gap-2 mb-1">
            <div className="flex items-center gap-1.5 flex-wrap">
              {n.is_pinned && <span className="text-[10px] font-bold uppercase tracking-wider bg-amber-500 text-white rounded-full px-2 py-0.5">Pinned</span>}
              <span className={`text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 ${catTone[n.category] || catTone.general}`}>{(n.category || 'general').replace('_', ' ')}</span>
            </div>
            <div className="flex items-center gap-1">
              <button type="button" onClick={() => setEditing(n)} className="text-[11px] font-semibold text-slate-700 hover:text-slate-900">Edit</button>
              <button type="button" onClick={() => remove(n.id)} className="text-[11px] font-semibold text-red-600 hover:text-red-800">Delete</button>
            </div>
          </div>
          <p className="text-sm text-slate-800 whitespace-pre-line">{n.body}</p>
          <p className="text-[10px] text-slate-400 mt-2">{n.created_by_name} · {fmtDateTime(n.created_at)}</p>
        </div>
      ))}

      {editing && (
        <Modal title={editing.id ? 'Edit note' : 'New note'} onClose={() => setEditing(null)}>
          <div className="space-y-3">
            <label className="block">
              <span className="text-[11px] font-bold uppercase tracking-wider text-slate-600">Body</span>
              <textarea
                rows={6}
                value={editing.body}
                onChange={(e) => setEditing({ ...editing, body: e.target.value })}
                data-testid="dhub-note-body"
                className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm"
              />
            </label>
            <label className="block">
              <span className="text-[11px] font-bold uppercase tracking-wider text-slate-600">Category</span>
              <select
                value={editing.category}
                onChange={(e) => setEditing({ ...editing, category: e.target.value })}
                data-testid="dhub-note-category"
                className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm"
              >
                {['general', 'credit', 'strategy', 'follow_up', 'compliance', 'post_settlement'].map((c) => (
                  <option key={c} value={c}>{c.replace('_', ' ')}</option>
                ))}
              </select>
            </label>
            <label className="inline-flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={!!editing.is_pinned} onChange={(e) => setEditing({ ...editing, is_pinned: e.target.checked })} />
              Pin to top
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setEditing(null)} className="rounded-full border border-slate-300 text-slate-800 text-sm font-semibold px-4 py-2">Cancel</button>
              <button type="button" onClick={save} data-testid="dhub-note-save" className="rounded-full bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold px-4 py-2">Save</button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

// ─── Audit Log panel (read-only feed of every mutation) ────────────
const AuditLogPanel = ({ clientId, headers }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [catFilter, setCatFilter] = useState('');
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/hub/clients/${clientId}/audit-log`, { headers });
        setItems(data.items || []);
      } catch { toast.error('Failed to load audit log'); }
      finally { setLoading(false); }
    })();
  }, [clientId, headers]);

  const cats = Array.from(new Set(items.map((i) => i.category))).filter(Boolean);
  const filtered = catFilter ? items.filter((i) => i.category === catFilter) : items;

  const catTone = {
    stage_change: 'bg-emerald-100 text-emerald-800',
    doc_note: 'bg-sky-100 text-sky-800',
    message: 'bg-slate-100 text-slate-800',
    system: 'bg-purple-100 text-purple-800',
    milestone: 'bg-amber-100 text-amber-800',
  };

  return (
    <>
      <div className="rounded-2xl bg-slate-50 border border-slate-200 p-3 flex items-start gap-2">
        <History className="w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-slate-700">
          Append-only history of every change made on this client&apos;s hub — visible to your team only.
        </p>
      </div>

      {cats.length > 0 && (
        <div className="flex items-center gap-1.5 overflow-x-auto pb-1">
          <button type="button" onClick={() => setCatFilter('')} className={`whitespace-nowrap rounded-full text-[11px] font-semibold px-3 py-1 ${!catFilter ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}>All ({items.length})</button>
          {cats.map((c) => (
            <button key={c} type="button" onClick={() => setCatFilter(catFilter === c ? '' : c)} data-testid={`dhub-audit-cat-${c}`}
              className={`whitespace-nowrap rounded-full text-[11px] font-semibold px-3 py-1 capitalize ${catFilter === c ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}>
              {c.replace('_', ' ')}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-8 text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <p className="text-xs text-slate-500 text-center py-6">No audit entries match.</p>
      ) : (
        <div className="space-y-1.5">
          {filtered.map((u) => (
            <div key={u.id} className="rounded-lg bg-white border border-slate-200 p-3" data-testid={`dhub-audit-${u.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-slate-900">{u.title}</p>
                  {u.message && <p className="text-xs text-slate-600 mt-0.5 whitespace-pre-line">{u.message}</p>}
                  <p className="text-[10px] text-slate-400 mt-1">
                    {u.posted_by_role} · {u.posted_by_name || 'system'} · {fmtDateTime(u.posted_at)}
                  </p>
                </div>
                <span className={`text-[9px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 whitespace-nowrap ${catTone[u.category] || 'bg-slate-100 text-slate-700'}`}>
                  {(u.category || 'system').replace('_', ' ')}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
};


// ─── Content Manager (FAQs + How-Tos) ──────────────────────────────
const ContentManager = ({ headers }) => {
  const [sub, setSub] = useState('faqs'); // faqs | howtos
  return (
    <div className="space-y-4">
      <div className="inline-flex items-center gap-1 rounded-full bg-white border border-slate-200 p-1">
        {[
          { key: 'faqs', label: 'FAQs', testId: 'dhub-content-faqs' },
          { key: 'howtos', label: 'How-To guides', testId: 'dhub-content-howtos' },
        ].map((v) => (
          <button
            key={v.key}
            type="button"
            onClick={() => setSub(v.key)}
            data-testid={v.testId}
            className={`rounded-full text-xs font-semibold px-3.5 py-1.5 transition-colors ${
              sub === v.key ? 'bg-slate-900 text-white' : 'text-slate-700 hover:text-slate-900'
            }`}
          >
            {v.label}
          </button>
        ))}
      </div>
      {sub === 'faqs' ? <FaqManager headers={headers} /> : <HowToManager headers={headers} />}
    </div>
  );
};

const FaqManager = ({ headers }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {id?, question, answer, published}
  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/hub/faqs`, { headers });
      setItems(data.items || []);
    } catch (e) { toast.error('Failed to load FAQs'); }
    finally { setLoading(false); }
  }, [headers]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!editing?.question?.trim() || !editing?.answer?.trim()) { toast.error('Question and answer required'); return; }
    try {
      const payload = { question: editing.question.trim(), answer: editing.answer.trim(), published: !!editing.published };
      if (editing.id) await axios.patch(`${API}/api/hub/faqs/${editing.id}`, payload, { headers });
      else await axios.post(`${API}/api/hub/faqs`, payload, { headers });
      toast.success('Saved');
      setEditing(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this FAQ?')) return;
    try {
      await axios.delete(`${API}/api/hub/faqs/${id}`, { headers });
      toast.success('Deleted');
      load();
    } catch (e) { toast.error('Failed'); }
  };

  const togglePublished = async (item) => {
    try {
      await axios.patch(`${API}/api/hub/faqs/${item.id}`, {
        question: item.question, answer: item.answer, published: !item.published,
      }, { headers });
      load();
    } catch (e) { toast.error('Failed'); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-slate-600">These FAQs appear in every client&apos;s Help tab.</p>
        <button
          type="button"
          onClick={() => setEditing({ question: '', answer: '', published: true })}
          data-testid="dhub-faq-new"
          className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold px-3 py-2"
        >
          <Plus className="w-3.5 h-3.5" /> Add FAQ
        </button>
      </div>
      {loading ? (
        <div className="flex items-center justify-center py-16 text-slate-500"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 rounded-2xl bg-white border border-slate-200">
          <HelpCircleIcon />
          <p className="text-sm text-slate-600 mt-2">No FAQs yet — add your first one.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((f) => (
            <div key={f.id} className={`rounded-xl border p-3 ${f.published ? 'bg-white border-slate-200' : 'bg-slate-50 border-slate-200 opacity-70'}`} data-testid={`dhub-faq-${f.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-bold text-slate-900">{f.question}</p>
                  <p className="text-xs text-slate-600 mt-1 whitespace-pre-line">{f.answer}</p>
                </div>
                <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full whitespace-nowrap ${f.published ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-200 text-slate-700'}`}>
                  {f.published ? 'Published' : 'Draft'}
                </span>
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                <button type="button" onClick={() => setEditing(f)} className="rounded-full border border-slate-300 hover:bg-slate-50 text-[11px] font-semibold px-2.5 py-1">Edit</button>
                <button type="button" onClick={() => togglePublished(f)} className="rounded-full border border-slate-300 hover:bg-slate-50 text-[11px] font-semibold px-2.5 py-1">{f.published ? 'Unpublish' : 'Publish'}</button>
                <button type="button" onClick={() => remove(f.id)} className="rounded-full border border-red-300 text-red-700 hover:bg-red-50 text-[11px] font-semibold px-2.5 py-1"><Trash2 className="w-3 h-3 inline" /> Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}
      {editing && (
        <Modal title={editing.id ? 'Edit FAQ' : 'Add FAQ'} onClose={() => setEditing(null)}>
          <div className="space-y-3">
            <Field label="Question" value={editing.question} onChange={(v) => setEditing({ ...editing, question: v })} testId="dhub-faq-question" required />
            <label className="block">
              <span className="text-[11px] font-bold uppercase tracking-wider text-slate-600">Answer</span>
              <textarea
                rows={5}
                value={editing.answer}
                onChange={(e) => setEditing({ ...editing, answer: e.target.value })}
                data-testid="dhub-faq-answer"
                className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm"
              />
            </label>
            <label className="inline-flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={!!editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} />
              Published (visible to clients)
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setEditing(null)} className="rounded-full border border-slate-300 text-slate-800 text-sm font-semibold px-4 py-2">Cancel</button>
              <button type="button" onClick={save} data-testid="dhub-faq-save" className="rounded-full bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold px-4 py-2">Save</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

const HowToManager = ({ headers }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/hub/howtos`, { headers });
      setItems(data.items || []);
    } catch (e) { toast.error('Failed to load How-Tos'); }
    finally { setLoading(false); }
  }, [headers]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!editing?.title?.trim() || !editing?.body?.trim()) { toast.error('Title and body required'); return; }
    try {
      const payload = {
        title: editing.title.trim(),
        body: editing.body.trim(),
        category: editing.category || null,
        video_url: editing.video_url || null,
        published: !!editing.published,
      };
      if (editing.id) await axios.patch(`${API}/api/hub/howtos/${editing.id}`, payload, { headers });
      else await axios.post(`${API}/api/hub/howtos`, payload, { headers });
      toast.success('Saved');
      setEditing(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this guide?')) return;
    try {
      await axios.delete(`${API}/api/hub/howtos/${id}`, { headers });
      toast.success('Deleted');
      load();
    } catch (e) { toast.error('Failed'); }
  };

  const togglePublished = async (item) => {
    try {
      await axios.patch(`${API}/api/hub/howtos/${item.id}`, {
        title: item.title, body: item.body,
        category: item.category, video_url: item.video_url,
        published: !item.published,
      }, { headers });
      load();
    } catch (e) { toast.error('Failed'); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-slate-600">Step-by-step guides visible in every client&apos;s Help tab.</p>
        <button
          type="button"
          onClick={() => setEditing({ title: '', body: '', category: '', video_url: '', published: true })}
          data-testid="dhub-howto-new"
          className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold px-3 py-2"
        >
          <Plus className="w-3.5 h-3.5" /> Add guide
        </button>
      </div>
      {loading ? (
        <div className="flex items-center justify-center py-16 text-slate-500"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 rounded-2xl bg-white border border-slate-200">
          <Sparkles className="w-8 h-8 mx-auto text-slate-400" />
          <p className="text-sm text-slate-600 mt-2">No guides yet — add your first one.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((h) => (
            <div key={h.id} className={`rounded-xl border p-3 ${h.published ? 'bg-white border-slate-200' : 'bg-slate-50 border-slate-200 opacity-70'}`} data-testid={`dhub-howto-${h.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  {h.category && <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-0.5">{h.category}</p>}
                  <p className="text-sm font-bold text-slate-900">{h.title}</p>
                  <p className="text-xs text-slate-600 mt-1 whitespace-pre-line line-clamp-3">{h.body}</p>
                  {h.video_url && <p className="text-[10px] text-slate-500 mt-1 truncate">🎬 {h.video_url}</p>}
                </div>
                <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full whitespace-nowrap ${h.published ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-200 text-slate-700'}`}>
                  {h.published ? 'Published' : 'Draft'}
                </span>
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                <button type="button" onClick={() => setEditing(h)} className="rounded-full border border-slate-300 hover:bg-slate-50 text-[11px] font-semibold px-2.5 py-1">Edit</button>
                <button type="button" onClick={() => togglePublished(h)} className="rounded-full border border-slate-300 hover:bg-slate-50 text-[11px] font-semibold px-2.5 py-1">{h.published ? 'Unpublish' : 'Publish'}</button>
                <button type="button" onClick={() => remove(h.id)} className="rounded-full border border-red-300 text-red-700 hover:bg-red-50 text-[11px] font-semibold px-2.5 py-1"><Trash2 className="w-3 h-3 inline" /> Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}
      {editing && (
        <Modal title={editing.id ? 'Edit How-To guide' : 'Add How-To guide'} onClose={() => setEditing(null)}>
          <div className="space-y-3">
            <Field label="Title" value={editing.title} onChange={(v) => setEditing({ ...editing, title: v })} testId="dhub-howto-title" required />
            <Field label="Category (optional)" value={editing.category || ''} onChange={(v) => setEditing({ ...editing, category: v })} testId="dhub-howto-category" placeholder="e.g. Documents, ID Verification, Settlement" />
            <label className="block">
              <span className="text-[11px] font-bold uppercase tracking-wider text-slate-600">Body (supports plain text with line breaks)</span>
              <textarea
                rows={7}
                value={editing.body}
                onChange={(e) => setEditing({ ...editing, body: e.target.value })}
                data-testid="dhub-howto-body"
                placeholder="Step-by-step instructions…"
                className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm"
              />
            </label>
            <Field label="Video URL (optional)" value={editing.video_url || ''} onChange={(v) => setEditing({ ...editing, video_url: v })} testId="dhub-howto-video" placeholder="https://…" />
            <label className="inline-flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={!!editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} />
              Published (visible to clients)
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setEditing(null)} className="rounded-full border border-slate-300 text-slate-800 text-sm font-semibold px-4 py-2">Cancel</button>
              <button type="button" onClick={save} data-testid="dhub-howto-save" className="rounded-full bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold px-4 py-2">Save</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

const HelpCircleIcon = () => (
  <svg className="w-8 h-8 mx-auto text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <circle cx="12" cy="12" r="9" strokeWidth="2" />
    <path d="M9 9a3 3 0 016 0c0 2-3 3-3 3M12 17h.01" strokeWidth="2" strokeLinecap="round" />
  </svg>
);
